import React, { useCallback, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from '../config/authConfig';
import API_CONFIG from '../config/apiConfig';

/**
 * Action row for the "organization / tenant not authorized" screens.
 *
 * Renders a "Request Access" button next to the screen's own button (Sign Out or
 * Switch Account). Requesting access emails the RIIMS administrator every detail
 * needed to allow-list the user's organization, so a blocked user has a way
 * forward instead of a dead-end sign-out button.
 *
 * The request is authenticated with whatever token the blocked account can still
 * produce: normally the API access token, but when the organization has not
 * consented to the API scope only an ID token exists, and that is exactly the
 * case that needs to ask for access.
 */
const AccessDeniedActions = ({
    reason,
    deniedMessage,
    secondaryLabel,
    onSecondary
}) => {
    const { instance, accounts } = useMsal();
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error
    const [statusMessage, setStatusMessage] = useState('');

    const account = accounts?.[0] || null;

    const requesterLabel = useMemo(() => {
        return account?.username || account?.idTokenClaims?.preferred_username || '';
    }, [account]);

    const acquireIdentityToken = useCallback(async () => {
        if (!account) return null;

        try {
            const result = await instance.acquireTokenSilent({ ...apiRequest, account });
            if (result?.accessToken) return result.accessToken;
        } catch (error) {
            // Expected when the organization has not consented to the API scope.
            console.warn('Access request: falling back to ID token,', error?.errorCode || error?.message);
        }

        return account.idToken || null;
    }, [instance, account]);

    const handleRequestAccess = useCallback(async () => {
        if (status === 'sending' || status === 'sent') return;

        setStatus('sending');
        setStatusMessage('');

        try {
            const token = await acquireIdentityToken();
            if (!token) {
                setStatus('error');
                setStatusMessage('We could not verify your sign-in. Please sign out and try again.');
                return;
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}/access-requests`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    reason: reason || 'tenant-not-allowlisted',
                    deniedMessage: deniedMessage || ''
                })
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                setStatus('error');
                setStatusMessage(payload?.message || 'We could not send your request. Please try again later.');
                return;
            }

            setStatus('sent');
            setStatusMessage(payload?.message || 'Your access request has been sent.');
        } catch (error) {
            console.error('Access request failed:', error);
            setStatus('error');
            setStatusMessage('We could not send your request. Please check your connection and try again.');
        }
    }, [status, acquireIdentityToken, reason, deniedMessage]);

    const isSent = status === 'sent';
    const isSending = status === 'sending';

    const primaryStyle = {
        backgroundColor: isSent ? '#198754' : '#0d6efd',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        padding: '12px 24px',
        fontSize: '16px',
        cursor: (isSent || isSending) ? 'default' : 'pointer',
        opacity: isSending ? 0.75 : 1,
        transition: 'background-color 0.2s ease'
    };

    const secondaryStyle = {
        backgroundColor: 'white',
        color: '#495057',
        border: '1px solid #ced4da',
        borderRadius: '6px',
        padding: '12px 24px',
        fontSize: '16px',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease'
    };

    return (
        <div>
            <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap'
            }}>
                <button
                    onClick={handleRequestAccess}
                    disabled={isSending || isSent}
                    style={primaryStyle}
                    onMouseEnter={(e) => {
                        if (!isSent && !isSending) e.currentTarget.style.backgroundColor = '#0b5ed7';
                    }}
                    onMouseLeave={(e) => {
                        if (!isSent && !isSending) e.currentTarget.style.backgroundColor = '#0d6efd';
                    }}
                >
                    {isSent && <i className="bi bi-check-lg" style={{ marginRight: '8px' }}></i>}
                    {isSending ? 'Sending Request...' : isSent ? 'Request Sent' : 'Request Access'}
                </button>

                {secondaryLabel && (
                    <button
                        onClick={onSecondary}
                        style={secondaryStyle}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                        {secondaryLabel}
                    </button>
                )}
            </div>

            {statusMessage && (
                <p style={{
                    marginTop: '16px',
                    marginBottom: 0,
                    color: status === 'error' ? '#b02a37' : '#198754',
                    fontSize: '14px',
                    lineHeight: 1.5
                }}>
                    {statusMessage}
                    {isSent && requesterLabel && (
                        <>
                            <br />
                            <span style={{ color: '#6c757d' }}>
                                We will follow up with {requesterLabel}.
                            </span>
                        </>
                    )}
                </p>
            )}
        </div>
    );
};

export default AccessDeniedActions;
